import { createTheme } from '@mui/material';

export const brandPalette = {
    blue: '#13154F',
    amber: '#FFA300',
    amberDark: '#E89400',
    gray: '#F5F6FB',
    graySecondary: '#E4E6F5',
    surface: '#F0F1FA',
    muted: '#4E5273',
    onNavy: '#EDEFF9'
};

export const fontFamily = '"Montserrat", "Segoe UI", system-ui, sans-serif';

const modeTokens = {
    light: {
        mode: 'light',
        background: brandPalette.gray,
        paper: '#ffffff',
        surface: brandPalette.surface,
        text: brandPalette.blue,
        muted: brandPalette.muted,
        line: brandPalette.graySecondary,
        input: '#ffffff',
        popup: '#ffffff',
        popupShadow: '0 8px 24px rgba(19, 21, 79, 0.12)',
        zebra: brandPalette.surface,
        hover: '#FFF1D6'
    },
    dark: {
        mode: 'dark',
        background: '#0e1040',
        paper: '#151850',
        surface: '#1a1d5c',
        text: brandPalette.onNavy,
        muted: '#b8bcda',
        line: 'rgba(237, 239, 249, 0.14)',
        input: '#1a1d5c',
        popup: '#1a1d5c',
        popupShadow: '0 8px 24px rgba(0, 0, 0, 0.32)',
        zebra: '#1a1d5c',
        hover: '#3a2d0f'
    }
};

const kpiLabel = {
    fontFamily,
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    lineHeight: 1.4,
    textTransform: 'uppercase'
};

export const createAppTheme = (mode = 'light') => {
    const tokens = modeTokens[mode] || modeTokens.light;
    const isDark = tokens.mode === 'dark';

    return createTheme({
        palette: {
            mode: tokens.mode,
            primary: {
                main: brandPalette.blue,
                dark: '#0b0d33',
                light: '#2a2d78',
                contrastText: '#ffffff'
            },
            secondary: {
                main: brandPalette.amber,
                dark: brandPalette.amberDark,
                contrastText: '#ffffff'
            },
            success: { main: '#1E7A4F' },
            error: { main: '#B4233A' },
            warning: { main: brandPalette.amber },
            info: { main: brandPalette.blue },
            background: {
                default: tokens.background,
                paper: tokens.paper
            },
            text: {
                primary: tokens.text,
                secondary: tokens.muted
            },
            divider: tokens.line
        },
        shape: {
            borderRadius: 15
        },
        typography: {
            fontFamily,
            fontSize: 15,
            h1: { fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.15 },
            h2: { fontWeight: 300, letterSpacing: '0.01em', lineHeight: 1.2 },
            h3: { fontWeight: 700, letterSpacing: '0.01em', lineHeight: 1.2 },
            h4: { fontWeight: 700, letterSpacing: 0, lineHeight: 1.2 },
            h5: { fontWeight: 700, letterSpacing: 0, lineHeight: 1.25 },
            h6: { fontWeight: 700, letterSpacing: 0, lineHeight: 1.3 },
            subtitle1: { fontWeight: 600 },
            subtitle2: { fontWeight: 600 },
            body1: { fontWeight: 400, fontSize: '0.95rem', lineHeight: 1.5 },
            body2: { fontWeight: 400, fontSize: '0.88rem', lineHeight: 1.5 },
            button: {
                fontWeight: 700,
                letterSpacing: 0,
                textTransform: 'none'
            },
            caption: { color: tokens.muted, fontSize: '0.76rem' },
            overline: { ...kpiLabel, color: tokens.muted }
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: tokens.background,
                        color: tokens.text
                    }
                }
            },
            MuiButton: {
                defaultProps: {
                    disableElevation: true
                },
                styleOverrides: {
                    root: {
                        borderRadius: 999,
                        boxShadow: 'none',
                        fontWeight: 700,
                        minHeight: 40,
                        padding: '8px 18px',
                        transition: 'background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease'
                    },
                    contained: {
                        background: brandPalette.amber,
                        color: '#ffffff',
                        '&:hover': {
                            background: brandPalette.amberDark,
                            boxShadow: 'none'
                        },
                        '&.Mui-disabled': {
                            background: tokens.line,
                            color: tokens.muted
                        }
                    },
                    outlined: {
                        borderColor: tokens.line,
                        color: tokens.text,
                        background: tokens.paper,
                        '&:hover': {
                            borderColor: brandPalette.blue,
                            backgroundColor: tokens.surface
                        }
                    },
                    text: {
                        color: tokens.text,
                        '&:hover': {
                            backgroundColor: tokens.surface
                        }
                    },
                    sizeSmall: {
                        minHeight: 34,
                        padding: '5px 14px',
                        fontSize: '0.82rem'
                    }
                }
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        color: tokens.text,
                        transition: 'background-color 0.18s ease',
                        '&:hover': {
                            backgroundColor: tokens.surface
                        }
                    }
                }
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        border: `1px solid ${tokens.line}`,
                        borderRadius: 15,
                        background: tokens.paper,
                        boxShadow: 'none',
                        color: tokens.text
                    }
                }
            },
            MuiPaper: {
                defaultProps: {
                    elevation: 0
                },
                styleOverrides: {
                    root: {
                        borderColor: tokens.line,
                        backgroundImage: 'none'
                    },
                    rounded: {
                        borderRadius: 15
                    }
                }
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        borderRadius: 999,
                        fontWeight: 600
                    }
                }
            },
            MuiFormControl: {
                styleOverrides: {
                    root: {
                        '& .MuiInputLabel-root': {
                            color: tokens.muted,
                            fontFamily,
                            fontWeight: 500,
                            '&.Mui-focused': {
                                color: brandPalette.blue
                            }
                        }
                    }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        background: tokens.input,
                        color: tokens.text,
                        fontFamily,
                        fontWeight: 500,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: tokens.line
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: brandPalette.muted
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: brandPalette.blue,
                            borderWidth: 1.5
                        }
                    },
                    input: {
                        '&::placeholder': {
                            color: tokens.muted,
                            opacity: 1
                        }
                    }
                }
            },
            MuiSelect: {
                styleOverrides: {
                    icon: {
                        color: tokens.muted
                    }
                }
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        border: `1px solid ${tokens.line}`,
                        borderRadius: 15,
                        background: tokens.popup,
                        boxShadow: tokens.popupShadow,
                        color: tokens.text
                    },
                    list: {
                        padding: 8
                    }
                }
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        borderRadius: 10,
                        color: tokens.text,
                        fontFamily,
                        fontWeight: 500,
                        '&:hover': {
                            backgroundColor: tokens.surface
                        },
                        '&.Mui-selected': {
                            backgroundColor: brandPalette.blue,
                            color: '#ffffff'
                        }
                    }
                }
            },
            MuiAutocomplete: {
                styleOverrides: {
                    paper: {
                        border: `1px solid ${tokens.line}`,
                        borderRadius: 15,
                        background: tokens.popup,
                        boxShadow: tokens.popupShadow,
                        color: tokens.text
                    },
                    listbox: {
                        padding: 8,
                        color: tokens.text,
                        '& .MuiAutocomplete-option': {
                            borderRadius: 10,
                            fontFamily,
                            fontWeight: 500
                        },
                        '& .MuiAutocomplete-option.Mui-focused': {
                            backgroundColor: tokens.surface
                        },
                        '& .MuiAutocomplete-option[aria-selected="true"]': {
                            backgroundColor: tokens.surface
                        }
                    },
                    noOptions: {
                        color: tokens.muted,
                        fontFamily,
                        fontWeight: 500
                    },
                    loading: {
                        color: tokens.muted
                    }
                }
            },
            MuiPickersPopper: {
                styleOverrides: {
                    paper: {
                        border: `1px solid ${tokens.line}`,
                        borderRadius: 15,
                        background: tokens.popup,
                        boxShadow: tokens.popupShadow,
                        color: tokens.text
                    }
                }
            },
            MuiPickersLayout: {
                styleOverrides: {
                    root: {
                        background: 'transparent',
                        color: tokens.text
                    }
                }
            },
            MuiDateCalendar: {
                styleOverrides: {
                    root: {
                        background: 'transparent',
                        color: tokens.text
                    }
                }
            },
            MuiPickersDay: {
                styleOverrides: {
                    root: {
                        color: tokens.text,
                        fontFamily,
                        fontWeight: 500,
                        '&:hover': {
                            backgroundColor: tokens.surface
                        },
                        '&.Mui-selected': {
                            backgroundColor: `${brandPalette.blue} !important`,
                            color: '#ffffff !important'
                        }
                    }
                }
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        background: brandPalette.blue,
                        borderRadius: 10,
                        color: '#ffffff',
                        fontFamily,
                        fontSize: '0.78rem',
                        fontWeight: 500
                    },
                    arrow: {
                        color: brandPalette.blue
                    }
                }
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        borderBottom: `1px solid ${tokens.line}`,
                        color: tokens.text,
                        fontFamily,
                        fontVariantNumeric: 'tabular-nums'
                    },
                    head: {
                        ...kpiLabel,
                        background: brandPalette.blue,
                        color: brandPalette.onNavy,
                        fontSize: '0.7rem',
                        borderBottom: 0
                    }
                }
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        '&:nth-of-type(odd):not(.MuiTableRow-head)': {
                            backgroundColor: tokens.zebra
                        }
                    }
                }
            },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        borderRadius: 15,
                        fontFamily,
                        fontWeight: 500
                    }
                }
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: {
                        borderRadius: 999,
                        backgroundColor: tokens.line
                    },
                    bar: {
                        borderRadius: 999,
                        backgroundColor: brandPalette.amber
                    }
                }
            },
            MuiCircularProgress: {
                styleOverrides: {
                    root: {
                        color: brandPalette.amber
                    }
                }
            },
            MuiFormControlLabel: {
                styleOverrides: {
                    root: {
                        '& .MuiSwitch-root + .MuiFormControlLabel-label': {
                            marginLeft: 10
                        }
                    },
                    label: {
                        fontFamily
                    }
                }
            },
            MuiSwitch: {
                styleOverrides: {
                    root: {
                        width: 46,
                        height: 30,
                        padding: 0,
                        overflow: 'visible'
                    },
                    switchBase: {
                        padding: 4,
                        transition: 'transform 0.18s ease',
                        '&.Mui-checked': {
                            color: '#ffffff',
                            transform: 'translateX(16px)'
                        },
                        '&.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: brandPalette.amber,
                            opacity: 1
                        }
                    },
                    thumb: {
                        width: 22,
                        height: 22,
                        boxShadow: 'none'
                    },
                    track: {
                        borderRadius: 999,
                        backgroundColor: isDark ? '#4e5273' : '#b8bcda',
                        opacity: 1
                    }
                }
            },
            MuiTabs: {
                styleOverrides: {
                    indicator: {
                        backgroundColor: brandPalette.amber,
                        height: 3,
                        borderRadius: 999
                    }
                }
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        fontFamily,
                        fontWeight: 600,
                        textTransform: 'none',
                        color: tokens.muted,
                        '&.Mui-selected': {
                            color: tokens.text
                        }
                    }
                }
            },
            MuiDataGrid: {
                styleOverrides: {
                    root: {
                        border: `1px solid ${tokens.line}`,
                        borderRadius: 15,
                        color: tokens.text,
                        fontFamily,
                        background: tokens.paper,
                        overflow: 'hidden',
                        '--DataGrid-rowBorderColor': tokens.line,
                        '& .MuiDataGrid-columnHeader': {
                            background: brandPalette.blue,
                            color: brandPalette.onNavy
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            ...kpiLabel,
                            fontSize: '0.68rem'
                        },
                        '& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer .MuiIconButton-root, & .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon .MuiIconButton-root, & .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': {
                            color: brandPalette.onNavy
                        },
                        '& .MuiDataGrid-columnSeparator': {
                            color: 'rgba(237, 239, 249, 0.28)'
                        },
                        '& .MuiDataGrid-row:nth-of-type(odd)': {
                            backgroundColor: tokens.zebra
                        },
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: tokens.hover
                        },
                        '& .MuiDataGrid-cell': {
                            borderColor: tokens.line,
                            fontVariantNumeric: 'tabular-nums'
                        },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                            outline: 'none'
                        },
                        '& .MuiDataGrid-footerContainer': {
                            borderColor: tokens.line
                        },
                        '& .MuiDataGrid-overlay': {
                            background: tokens.paper
                        }
                    },
                    columnHeaders: {
                        background: brandPalette.blue,
                        color: brandPalette.onNavy,
                        borderBottom: 0
                    }
                }
            }
        }
    });
};

export const theme = createAppTheme('light');
